import { Link, createFileRoute } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'

import { workspacesQueryOptions } from '#/api/workspaces'
import type { WorkspaceStatus } from '#/api/workspaces'
import { useCan } from '#/user/me'
import { formatWeekdayDate } from '#/lib/format'
import { SECTION_LINK_CLASS, SectionHeader } from '#/components/page-header'
import { HeroField } from '#/components/brand/hero-field'
import { AskBar } from '#/components/ask-annwn/ask-bar'
import {
  NewWorkspaceCard,
  WorkspaceCard,
} from '#/components/workspaces/workspace-card'
import { PageContainer } from '#/components/page-container'
import { RoutePending } from '#/components/route-pending'
import { RouteError } from '#/components/route-error'

export const Route = createFileRoute('/_authed/accounts/$accountId/dashboard')({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(
      workspacesQueryOptions(params.accountId),
    ),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: Dashboard,
})

// Workspaces still being worked on lead the grid; finished ones follow.
const IN_FLIGHT_STATUSES = new Set<WorkspaceStatus>([
  'created',
  'initialised',
  'active',
])
const MAX_WORKSPACE_CARDS = 6

function greetingKey(hour: number) {
  if (hour < 12) return 'morning' as const
  if (hour < 18) return 'afternoon' as const
  return 'evening' as const
}

// The account's home: a greeting over the living field, Ask Annwn up front,
// and the workspaces to pick up.
function Dashboard() {
  const { t, i18n } = useTranslation()
  const { accountId } = Route.useParams()
  const { me, account } = Route.useRouteContext()
  const canAsk = useCan('annwn:chats:create')
  const canCreateWorkspace = useCan('annwn:workspaces:manage')
  const { data: workspaces } = useSuspenseQuery(
    workspacesQueryOptions(accountId),
  )
  const ordered = [
    ...workspaces.filter((w) => IN_FLIGHT_STATUSES.has(w.status)),
    ...workspaces.filter((w) => !IN_FLIGHT_STATUSES.has(w.status)),
  ].slice(0, MAX_WORKSPACE_CARDS)
  const now = new Date()

  return (
    <div className="flex flex-col">
      <header className="relative isolate overflow-hidden bg-primary-hover">
        <HeroField />
        <PageContainer className="relative min-h-56 justify-end gap-2 pt-12 pb-9">
          <p className="text-body-sm text-primary-foreground/70">
            {[account.name, formatWeekdayDate(now, i18n.language)]
              .filter(Boolean)
              .join(' · ')}
          </p>
          <h1 className="font-serif text-display-xl text-primary-foreground sm:text-display-hero">
            {t(`dashboard.greeting.${greetingKey(now.getHours())}`, {
              name: me.first_name ?? me.email,
            })}
          </h1>
        </PageContainer>
      </header>

      <PageContainer className="gap-14">
        {canAsk ? <AskBar accountId={accountId} /> : null}

        <section
          aria-labelledby="dashboard-workspaces"
          className="flex min-w-0 flex-col gap-5"
        >
          <SectionHeader
            id="dashboard-workspaces"
            title={t('dashboard.yourWorkspaces')}
            action={
              <Link
                to="/accounts/$accountId/workspaces"
                params={{ accountId }}
                className={SECTION_LINK_CLASS}
              >
                {t('dashboard.allWorkspaces')}
                <ArrowRight aria-hidden="true" />
              </Link>
            }
          />
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 motion-safe:animate-in motion-safe:duration-300 motion-safe:ease-out motion-safe:fade-in motion-safe:slide-in-from-bottom-1">
            {ordered.map((workspace) => (
              <li key={workspace.uuid}>
                <WorkspaceCard accountId={accountId} workspace={workspace} />
              </li>
            ))}
            {canCreateWorkspace ? (
              <li>
                <NewWorkspaceCard accountId={accountId} />
              </li>
            ) : null}
          </ul>
        </section>
      </PageContainer>
    </div>
  )
}
